import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  ClipboardList,
  ShieldCheck,
  Award,
  Sparkles,
  Route,
  AlertTriangle,
  Users,
  Wallet,
  Cpu,
  RefreshCw,
  Activity,
  Workflow,
  GraduationCap,
  Gavel,
  Gauge,
  BarChart3,
} from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import MaskedReveal from "@/components/motion/MaskedReveal";
import HexMark from "@/components/motion/HexMark";
import Seo, { buildBreadcrumbs } from "@/components/Seo";

// ─────────────────────────────────────────────────────────────
// Editorial layout: "PM Board" — a PMBOK-style knowledge-area
// board where each of the 12 typical PM tasks is a swim-lane
// card with 3 sub-bullets. Distinct from journey rails or dossier
// ToC used elsewhere.
// ─────────────────────────────────────────────────────────────

const TASKS = [
  {
    id: "plans",
    icon: Route,
    lane: "Initiating",
    title: "Developing Project Plans & Strategies",
    items: [
      "Needs assessments to determine project requirements",
      "Establishing project milestones and deliverables",
      "Contingency plans for potential project setbacks",
    ],
  },
  {
    id: "risk",
    icon: AlertTriangle,
    lane: "Planning",
    title: "Risk Management",
    items: [
      "Risk assessments and impact analyses",
      "Response plans with named risk owners",
      "Regular risk reviews and updates",
    ],
  },
  {
    id: "stakeholders",
    icon: Users,
    lane: "Planning",
    title: "Engaging Stakeholders",
    items: [
      "Mapping stakeholder interests and influence",
      "Stakeholder engagement plans",
      "Workshops and feedback sessions",
    ],
  },
  {
    id: "budget",
    icon: Wallet,
    lane: "Planning",
    title: "Controlling Budgets",
    items: [
      "Line-by-line cost tracking",
      "Change-order pricing & approvals",
      "Reforecasting against baseline",
    ],
  },
  {
    id: "tech",
    icon: Cpu,
    lane: "Executing",
    title: "Integrating Technology",
    items: [
      "Assessing tech needs and requirements",
      "Selecting and implementing software solutions",
      "Technical support and troubleshooting",
    ],
  },
  {
    id: "change",
    icon: RefreshCw,
    lane: "Executing",
    title: "Facilitating Changes",
    items: [
      "Change-management plans & communications strategy",
      "Impact assessments for proposed changes",
      "Training team members on new processes",
    ],
  },
  {
    id: "monitor",
    icon: Activity,
    lane: "Monitoring",
    title: "Monitoring & Controlling Projects",
    items: [
      "PM software for progress tracking",
      "Regular project status meetings",
      "Adjusting plans and schedules as needed",
    ],
  },
  {
    id: "processes",
    icon: Workflow,
    lane: "Monitoring",
    title: "Creating & Controlling Processes",
    items: [
      "Process mapping and analysis",
      "Identifying bottlenecks and inefficiencies",
      "Process-improvement initiatives",
    ],
  },
  {
    id: "training",
    icon: GraduationCap,
    lane: "Executing",
    title: "Training Teams",
    items: [
      "Training materials and resources",
      "Workshops and training sessions",
      "Evaluating effectiveness · continuous improvement",
      "Taught by certified instructor(s)",
    ],
  },
  {
    id: "compliance",
    icon: Gavel,
    lane: "Monitoring",
    title: "Ensuring Compliance",
    items: [
      "Compliance audits and reviews",
      "Policies and procedures development",
      "Training on regulatory requirements",
    ],
  },
  {
    id: "operations",
    icon: Gauge,
    lane: "Executing",
    title: "Enhancing Operations",
    items: [
      "Operational data analysis for improvement",
      "Lean principles implementation",
      "Supply chain & logistics optimization",
    ],
  },
  {
    id: "reporting",
    icon: BarChart3,
    lane: "Closing",
    title: "Reporting Performance",
    items: [
      "Dashboards and visual reports for stakeholders",
      "Performance-data trend analysis",
      "Recommendations for improvement",
    ],
  },
];

const CREDENTIALS = [
  { code: "PMP", label: "Project Management Professional · PMI" },
  { code: "ATP-I", label: "Authorized Training Partner Instructor · PMI" },
  { code: "CMP", label: "Certified Meeting Professional" },
  { code: "CMM", label: "Certification in Meeting Management" },
];

const CLIENTS = [
  "Merrill Lynch",
  "Pfizer",
  "Bank of America",
  "Temple University",
  "Pillar College",
  "Novartis",
];

export default function ProjectManagement() {
  return (
    <div className="bg-[var(--bg)] text-[var(--text)]">
      <Seo
        title="Project Management — Certified PMP · ATP-I Instructor"
        description="Professional project management by a certified PMP and PMI Authorized Training Partner Instructor. Twelve knowledge areas — from planning to closing — delivered with precision and excellence."
        path="/services/project-management"
        keywords="project management, PMP certified, PMI ATP instructor, risk management, stakeholder engagement, project planning, project controls, PMO consulting"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Project Management",
            serviceType: "Professional Project Management",
            provider: { "@type": "Organization", name: "Magee Enterprises" },
            areaServed: "Global",
            description:
              "Certified PMP and PMI Authorized Training Partner Instructor delivering all 12 knowledge areas — planning, risk, stakeholder engagement, budget control, and reporting.",
          },
          buildBreadcrumbs([
            ["Home", "/"],
            ["Services", "/services"],
            ["Project Management", "/services/project-management"],
          ]),
        ]}
      />

      {/* ============ HERO ============ */}
      <section className="relative pt-36 md:pt-44 pb-16 md:pb-24 border-b border-[var(--border)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-45 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[560px] h-[560px] -top-32 -right-24 opacity-40" />
        <HexMark
          size={260}
          opacity={0.055}
          className="absolute -left-10 top-24 rotate-[10deg] pointer-events-none hidden md:block"
        />
        <span className="corner-cross" style={{ top: 40, right: 40 }} />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="flex items-center gap-4 mb-10 flex-wrap">
              <span className="w-10 h-px bg-[var(--text)]/40" />
              <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--text-muted)]">
                Project Management · Certified
              </span>
              <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-cyan-700 border border-cyan-500/30 rounded-full px-2.5 py-1 bg-cyan-500/5 inline-flex items-center gap-1.5">
                <Award size={11} /> PMI-NJ · Project Manager of the Year
              </span>
            </div>
            <MaskedReveal
              lines={["Partner with", "excellence."]}
              className="font-editorial tracking-[-0.03em] leading-[0.94] text-[13vw] md:text-[8vw] lg:text-[7vw]"
              stagger={0.09}
            />
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-col justify-end">
            <Reveal delay={0.35}>
              <p className="text-[var(--text)]/75 text-lg leading-relaxed">
                A certified PMP service — voted{" "}
                <span className="text-[var(--text)] font-medium italic">
                  &ldquo;Project Manager of the Year&rdquo;
                </span>{" "}
                by the Project Management Institute&nbsp;— NJ Chapter. Twelve
                knowledge areas, one accountable owner.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="btn-primary"
                  data-testid="pm-hero-cta-quote"
                >
                  Request a proposal <ArrowRight size={14} />
                </Link>
                <a
                  href="#board"
                  className="btn-dark"
                  data-testid="pm-hero-cta-explore"
                >
                  See the board <ArrowUpRight size={14} />
                </a>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Credential strip */}
        <div className="relative mt-16 md:mt-20 max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal delay={0.15}>
            <div className="pt-8 border-t border-[var(--border)]">
              <div className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)] mb-5">
                Credentials on file
              </div>
              <div
                data-testid="pm-credentials"
                className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
              >
                {CREDENTIALS.map((c) => (
                  <div
                    key={c.code}
                    className="relative border border-[var(--border)] bg-white rounded-2xl p-5 md:p-6 flex items-center gap-4 hover:border-[var(--accent)]/40 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-sky-600 text-white flex items-center justify-center shadow-lg shrink-0">
                      <ShieldCheck size={20} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-editorial text-xl leading-none tracking-tight">
                        {c.code}
                      </div>
                      <div className="mt-1.5 text-[11.5px] leading-snug text-[var(--text-muted)]">
                        {c.label}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ HERO IMAGE ============ */}
      <section className="relative -mt-4 md:-mt-8 pb-16 md:pb-24">
        <div className="max-w-[1600px] mx-auto px-4 md:px-10">
          <Reveal>
            <figure className="relative rounded-3xl overflow-hidden border border-[var(--border)] shadow-[0_30px_80px_-40px_rgba(6,60,120,0.45)]">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1920&q=80"
                alt="Certified project management team at Magee Enterprises"
                loading="lazy"
                className="w-full h-[42vh] md:h-[60vh] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
              <figcaption className="absolute bottom-5 left-5 md:bottom-8 md:left-10 max-w-md">
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-300 bg-black/40 backdrop-blur px-2.5 py-1 rounded-full border border-cyan-300/30 inline-block">
                  PMP · ATP-I · one accountable owner
                </span>
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* ============ PROOF BAND (partner + trust) ============ */}
      <section className="relative py-16 md:py-24 overflow-hidden bg-[var(--surface)]">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 md:gap-14 items-center">
          <div className="col-span-12 lg:col-span-6">
            <Reveal>
              <span className="chip">
                <Sparkles size={12} /> Authorized Training Partner Instructor
              </span>
              <h2 className="mt-6 font-editorial text-3xl md:text-5xl leading-[0.98] tracking-[-0.03em]">
                We practice what we&rsquo;ve{" "}
                <span className="italic text-cyan-600 font-medium">taught.</span>
              </h2>
              <p className="mt-6 text-[var(--text-muted)] leading-relaxed max-w-lg">
                Sir Dion Magee is an <span className="text-[var(--text)] font-medium">Authorized Training Partner Instructor</span>{" "}
                credentialed by the Project Management Institute — meaning
                he has demonstrated the skill and expertise to prepare
                candidates for the PMP&reg; certification exam. The same
                discipline that shapes the classroom shapes every engagement.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="https://www.credly.com/badges/e6b203e2-5776-422b-b94b-292302186b6f"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-mono-brand text-[11px] uppercase tracking-[0.22em] text-[var(--text)] hover:text-[var(--accent)] transition-colors"
                  data-testid="pm-credly-link"
                >
                  Verify on Credly <ArrowUpRight size={13} />
                </a>
              </div>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-6">
            <Reveal delay={0.15}>
              <div className="relative card-white p-6 md:p-10">
                <span className="corner-cross" style={{ top: 14, right: 14 }} />
                <div className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)] mb-5">
                  Clients who&rsquo;ve experienced the PM service
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {CLIENTS.map((c) => (
                    <div
                      key={c}
                      className="flex items-center gap-3 rounded-xl bg-[var(--surface)] border border-[var(--border)] px-4 py-3"
                    >
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-gradient-to-br from-cyan-400 to-sky-600" />
                      <span className="text-sm text-[var(--text)]/85">{c}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ PM BOARD (12 knowledge areas) ============ */}
      <section
        id="board"
        className="relative py-20 md:py-32 overflow-hidden scroll-mt-28"
      >
        <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
        <div className="brand-bloom brand-bloom-blue w-[500px] h-[500px] top-24 -left-32 opacity-30" />
        <HexMark
          size={220}
          opacity={0.06}
          className="absolute -right-8 top-36 rotate-[16deg] pointer-events-none hidden lg:block"
        />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
              <div>
                <span className="chip">
                  <ClipboardList size={12} /> The PM Board · 12 disciplines
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                  Every task —{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    named, owned, tracked.
                  </span>
                </h2>
              </div>
              <p className="max-w-sm text-[var(--text-muted)]">
                The twelve knowledge areas we run day-to-day. Each with a lane
                (Initiating → Closing) and three sub-tasks per lane.
              </p>
            </div>
          </Reveal>

          <div
            data-testid="pm-board"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5"
          >
            {TASKS.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.04}>
                <article
                  data-testid={`pm-task-${t.id}`}
                  className="group relative bg-white border border-[var(--border)] rounded-2xl p-5 md:p-6 h-full hover:border-[var(--accent)]/45 hover:shadow-[0_16px_40px_-18px_rgba(6,182,212,0.4)] hover:-translate-y-0.5 transition-all duration-500"
                >
                  <span className="corner-cross" style={{ top: 10, right: 10 }} />
                  <div className="flex items-center gap-3 mb-4">
                    <div className="icon-tile !w-11 !h-11">
                      <t.icon size={18} />
                    </div>
                    <span className="font-mono-brand text-[9.5px] uppercase tracking-[0.22em] text-cyan-700 border border-cyan-500/25 bg-cyan-500/5 rounded-full px-2 py-1">
                      {t.lane}
                    </span>
                  </div>
                  <div className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                    /{String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-1 font-editorial text-lg md:text-xl leading-tight tracking-tight text-[var(--text)]">
                    {t.title}
                  </h3>
                  <ul className="mt-4 space-y-2">
                    {t.items.map((it) => (
                      <li
                        key={it}
                        className="flex items-start gap-2.5 text-[13px] leading-snug text-[var(--text-muted)]"
                      >
                        <span className="mt-1.5 shrink-0 inline-block w-1 h-1 rounded-full bg-gradient-to-br from-cyan-400 to-sky-600" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CLOSING CTA (dark) ============ */}
      <section className="relative py-20 md:py-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="card-dark relative overflow-hidden p-8 md:p-16">
            <div className="absolute inset-0 grid-pattern-dark opacity-30" />
            <div className="absolute -top-24 -left-20 w-[380px] h-[380px] rounded-full bg-cyan-500/20 blur-3xl" />
            <div className="absolute -bottom-24 -right-20 w-[420px] h-[420px] rounded-full bg-sky-500/20 blur-3xl" />
            <div className="relative grid grid-cols-12 gap-8 items-end">
              <div className="col-span-12 lg:col-span-8 text-white">
                <span className="chip !bg-white/10 !text-cyan-300 !border-cyan-300/25">
                  <ClipboardList size={12} /> Ready for a certified partner
                </span>
                <h3 className="mt-6 font-editorial text-3xl md:text-5xl lg:text-6xl leading-[0.98] tracking-[-0.03em]">
                  Bring us the project.{" "}
                  <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-cyan-400 bg-clip-text text-transparent italic font-medium">
                    We&rsquo;ll bring the plan.
                  </span>
                </h3>
                <p className="mt-6 text-white/70 max-w-xl leading-relaxed">
                  Logistics, tech, meetings, marketing, operations, launches — a scoped
                  conversation with your goals in mind.
                </p>
              </div>
              <div className="col-span-12 lg:col-span-4 flex flex-col sm:flex-row lg:justify-end gap-3">
                <Link
                  to="/contact"
                  className="btn-primary justify-center sm:justify-start w-full sm:w-auto"
                  data-testid="pm-footer-cta-quote"
                >
                  Request a proposal <ArrowRight size={14} />
                </Link>
                <Link
                  to="/contact#book"
                  className="btn-dark backdrop-blur justify-center sm:justify-start w-full sm:w-auto"
                  data-testid="pm-footer-cta-book"
                >
                  Book a call <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
